import { useTranslation } from "react-i18next"

import type { MediaJob } from "@/features/studio-ai/types/studio-ai.types"

import { CircleHelp, Film, ImageIcon, Palette, Pencil } from "@/components/icons"
import { LaunchTile } from "@/components/layouts/launch-tile"
import { Button } from "@/components/ui/button"
import { SettingsService } from "@/services/settings-service"

import { CreateMediaButton } from "./create-media-button"
import { StudioAIGalleryTabs } from "./studio-ai-gallery-tabs"
import { StudioAIGreeting } from "./studio-ai-greeting"

type StudioAILandingProps = {
  jobs: MediaJob[]
  isLoadingJobs: boolean
  onCreateImages: () => void
  onCreateStyle: () => void
  onCreateVideos: () => void
  onEditImages: () => void
  onRefreshJobs: () => void
  onOpenWelcome: () => void
}

export function StudioAILanding({
  jobs,
  isLoadingJobs,
  onCreateImages,
  onCreateStyle,
  onCreateVideos,
  onEditImages,
  onRefreshJobs,
  onOpenWelcome,
}: StudioAILandingProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi" })

  const settingsService = SettingsService.getInstance()
  const userName = settingsService.claims.name

  return (
    <div className="relative flex flex-col items-center gap-8">
      <Button className="absolute top-0 right-0 min-h-10" variant="outline" onClick={onOpenWelcome}>
        <CircleHelp aria-hidden="true" className="size-4" />
        {t("welcomeModal.openAction")}
      </Button>
      <h1 className="text-foreground text-4xl font-semibold tracking-tight">{t("title")}</h1>

      {/* Personalized greeting */}
      <StudioAIGreeting userName={userName} />

      {/* Action buttons */}
      <div className="grid w-full max-w-3xl grid-cols-2 gap-4 sm:grid-cols-3">
        <CreateMediaButton
          description={t("landing.createImagesDesc")}
          icon={ImageIcon}
          title={t("landing.createImages")}
          onClick={onCreateImages}
        />
        <CreateMediaButton
          description={t("landing.editImagesDesc")}
          icon={Pencil}
          title={t("landing.editImages")}
          onClick={onEditImages}
        />
        <CreateMediaButton
          description={t("landing.createVideosDesc")}
          icon={Film}
          title={t("landing.createVideos")}
          onClick={onCreateVideos}
        />
      </div>

      {/* Style template tile */}
      <LaunchTile
        className="w-full max-w-3xl"
        description={t("landing.createStyleDesc")}
        icon={Palette}
        orientation="horizontal"
        title={t("landing.createStyle")}
        onClick={onCreateStyle}
      />

      {/* Gallery tabs */}
      <div className="w-full">
        <StudioAIGalleryTabs isLoading={isLoadingJobs} jobs={jobs} onRefresh={onRefreshJobs} />
      </div>
    </div>
  )
}
